import { ENV } from '../../../config/runtimeEnv';
import { WorkflowTemplate } from "../../../types/appointments/ConsultWorkflowTypes";
import type { UserUuid } from "../../../types/identity";
import { http } from "../../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

// Workflow Template endpoints
export const fetchWorkflowTemplates = async () => {
	const response = await http.get(`${API_BASE_URL}/practitioner/workflow-templates`, {
		params: { include_inactive: true },
	});
	return response.data;
};

export const fetchWorkflowTemplate = async (id: number) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/workflow-templates/${id}`
	);
	return response.data;
};

export const createWorkflowTemplate = async (data: WorkflowTemplate) => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/workflow-templates`,
		data
	);
	return response.data;
};

export const updateWorkflowTemplate = async (id: number, data: WorkflowTemplate) => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/workflow-templates/${id}`,
		data
	);
	return response.data;
};

export const deleteWorkflowTemplate = async (id: number) => {
	const response = await http.delete(
		`${API_BASE_URL}/practitioner/workflow-templates/${id}`
	);
	return response.data;
};

export const duplicateWorkflowTemplate = async (id: number) => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/workflow-templates/${id}/duplicate`,
		{}
	);
	return response.data;
};

export const previewWorkflowTemplate = async (id: number) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/workflow-templates/${id}/preview`
	);
	return response.data;
};

// Consultation Job endpoints
export const generateConsultation = async (
	appointmentId: number,
	options?: {
		workflowTemplateId?: number;
		practitionerOverrideId?: UserUuid;
	}
) => {
	const body: Record<string, any> = {};
	if (options?.workflowTemplateId) {
		body.workflow_template_id = options.workflowTemplateId;
	}
	if (options?.practitionerOverrideId) {
		body.practitioner_override_id = options.practitionerOverrideId;
	}
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointments/${appointmentId}/consults/generate`,
		body
	);
	return response.data;
};

export const fetchConsultJobStatus = async (jobUuid: string) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/consults/${jobUuid}/status`
	);
	return response.data;
};

export const fetchSessionByJobUuid = async (jobUuid: string) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/consults/${jobUuid}/session`
	);
	return response.data as { job_uuid: string; status: string; session: any | null };
};

export const fetchSessionByJobId = async (jobId: number) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/consults-by-id/${jobId}/session`
	);
	return response.data as { job_id: number; job_uuid: string; status: string; session: any | null };
};

export const fetchPendingConsultJobs = async (appointmentId: number) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointments/${appointmentId}/consults`
	);
	return response.data;
};

export const cancelConsultJob = async (jobUuid: string) => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/consults/${jobUuid}/cancel`,
		{}
	);
	return response.data;
};

export const retryConsultJob = async (jobUuid: string) => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/consults/${jobUuid}/retry`,
		{}
	);
	return response.data;
};

// Consultation Session endpoints
export const fetchConsultSession = async (appointmentId: number) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/consult-sessions/${appointmentId}`
	);
	return response.data;
};

export const updateConsultSessionNotes = async (
	sessionId: number,
	stepKey: string,
	notes: string
) => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/consult-sessions/${sessionId}/notes`,
		{
			step_key: stepKey,
			notes: notes,
		}
	);
	return response.data;
};

export const markTopicDiscussed = async (
	sessionId: number,
	stepKey: string,
	isDiscussed: boolean
) => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/consult-sessions/${sessionId}/topics/${stepKey}/discussed`,
		{
			is_discussed: isDiscussed,
		}
	);
	return response.data;
};

export const exportConsultSession = async (
	sessionId: number,
	format: "pdf" | "docx"
) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/consult-sessions/${sessionId}/export`,
		{
			params: { format },
			responseType: "blob",
		}
	);
	return response.data;
};

// Workflow-Appointment Type Assignment endpoints
export const fetchAppointmentTypeWorkflows = async (appointmentTypeId: number) => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/workflows`
	);
	return response.data;
};

export const assignWorkflowToAppointmentType = async (
	appointmentTypeId: number,
	workflowTemplateId: number,
	isActive: boolean = true,
	autoGenerate: boolean = false
) => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/workflows`,
		{
			workflow_template_id: workflowTemplateId,
			is_active: isActive,
			auto_generate: autoGenerate,
		}
	);
	return response.data;
};

export const updateWorkflowAssignment = async (
	appointmentTypeId: number,
	assignmentId: number,
	isActive?: boolean,
	autoGenerate?: boolean
) => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/workflows/${assignmentId}`,
		{
			...(isActive !== undefined && { is_active: isActive }),
			...(autoGenerate !== undefined && { auto_generate: autoGenerate }),
		}
	);
	return response.data;
};

export const removeWorkflowAssignment = async (
	appointmentTypeId: number,
	assignmentId: number
) => {
	const response = await http.delete(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/workflows/${assignmentId}`
	);
	return response.data;
};


